import { useEffect, useMemo, useState } from "react";
import {
  Check,
  Eye,
  Layers,
  Loader2,
  ShieldCheck,
  Server,
  Square,
} from "lucide-react";
import { ReactFlowProvider } from "@xyflow/react";

import { Modal } from "../../../components/Modal";
import { useSspInheritance } from "../../../../hooks/useSspInheritance";
import {
  InheritanceFlowView,
  type AppData,
} from "../../InheritanceFlowView";
import type {
  InheritedComponent,
  InheritedService,
} from "../../../../api/frameworksApi";

/* ─────────────────────────────────────────────────────────
   Types
───────────────────────────────────────────────────────── */
type Mode = "application" | "service";
type LayerKind = "ssp" | "component";
/** Bucket the item belongs to — used by the picker card to split chips
 *  into Direct vs Service-derived sub-sections inside the same card. */
type ItemBucket = "direct" | "service";

interface LayerItem {
  id: string;
  kind: LayerKind;
  title: string;
  layer: string;
  description: string;
  components: string[];
  /** Which sub-section in the layer card this chip belongs to. */
  bucket?: ItemBucket;
  /** Parent service name when bucket === "service" — surfaced as a small
   *  tag on the chip so the user can see which service it came from. */
  parentService?: string;
  /** When false, the chip is rendered as disabled (e.g. service has no
   *  SSP yet). Hovering shows the disabled-reason tooltip. */
  isSelectable?: boolean;
  /** Reason text rendered in the hover/click tooltip for disabled chips. */
  disabledReason?: string;
  /** Linked SSP titles (when present) — surfaced in the tooltip so the
   *  user can see which SSPs already inherit from this service. */
  linkedSsps?: { sspId: string; title: string }[];
}

interface Cylinder {
  key: string;
  label: string;
  tone: Tone;
  items: LayerItem[];
}

interface Tone {
  border: string;
  ring: string;
  glow: string;
  bgFrom: string;
  bgVia: string;
  bgTo: string;
  labelText: string;
  labelDot: string;
  selectedBorder: string;
  selectedBg: string;
  radioBorder: string;
  radioFill: string;
}

// Service tone — violet. Brightened labelText so it stays legible on a
// dark card background.
const indigoTone: Tone = {
  border: "rgba(139, 92, 246, 0.45)",
  ring: "rgba(139, 92, 246, 0.12)",
  glow: "rgba(139, 92, 246, 0.22)",
  bgFrom: "rgba(139, 92, 246, 0.10)",
  bgVia: "rgba(139, 92, 246, 0.10)",
  bgTo: "rgba(139, 92, 246, 0.10)",
  labelText: "rgb(167, 139, 250)",
  labelDot: "rgb(139, 92, 246)",
  selectedBorder: "border-violet-500/70 dark:border-violet-400/70",
  selectedBg: "bg-violet-50/70 dark:bg-violet-500/10",
  radioBorder: "border-violet-500 dark:border-violet-400",
  radioFill: "bg-violet-500 dark:bg-violet-400",
};

// Component tone — cyan/sky. Distinct from Services so the two categories
// inside the same layer card read as different concepts at a glance.
const cyanTone: Tone = {
  border: "rgba(34, 211, 238, 0.45)",
  ring: "rgba(34, 211, 238, 0.12)",
  glow: "rgba(34, 211, 238, 0.22)",
  bgFrom: "rgba(34, 211, 238, 0.10)",
  bgVia: "rgba(34, 211, 238, 0.10)",
  bgTo: "rgba(34, 211, 238, 0.10)",
  labelText: "rgb(103, 232, 249)",
  labelDot: "rgb(34, 211, 238)",
  selectedBorder: "border-cyan-500/70 dark:border-cyan-400/70",
  selectedBg: "bg-cyan-50/70 dark:bg-cyan-500/10",
  radioBorder: "border-cyan-500 dark:border-cyan-400",
  radioFill: "bg-cyan-500 dark:bg-cyan-400",
};

interface ServiceProviderSsp {
  serviceId: string;
  sspId: string | null;
  isPublished: boolean;
}

interface Props {
  open: boolean;
  onClose: () => void;
  /** When provided, renders a back arrow in the modal header. Defaults to onClose. */
  onBack?: () => void;
  onConfirm: (
    selectedIds: string[],
    meta: { applicationId?: string; serviceId?: string; providerSspIds?: string[] },
  ) => void;
  mode: Mode;
  /** App code chosen on the previous step (Application mode only). */
  selectedAppId?: string;
  /** Service id chosen on the previous step (Service mode only). */
  selectedServiceId?: string;
  creating?: boolean;
  /** Provider SSP entries for each service — used to derive Leveraged Authorization IDs when the user picks services. */
  serviceProviderSsps?: ServiceProviderSsp[];
}

export function InheritancePickerModal({
  open,
  onClose,
  onBack,
  onConfirm,
  mode,
  selectedAppId = "",
  selectedServiceId = "",
  creating = false,
  serviceProviderSsps = [],
}: Props) {
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [previewId, setPreviewId] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState(false);

  const { data: inheritance, loading: inheritanceLoading } = useSspInheritance(open ? mode : null);

  // Reset transient state whenever the modal closes.
  useEffect(() => {
    if (!open) {
      setSelectedIds([]);
      setPreviewId(null);
      setShowPreview(false);
    }
  }, [open]);

  // Auto-select all selectable services on load (Application mode):
  // services with isUsed=true are eligible for inheritance and are
  // pre-ticked so the user only un-checks the ones they don't want.
  // Disabled (isUsed=false) services aren't selectable anyway.
  useEffect(() => {
    if (!open) return;
    if (mode === "application") {
      const app = inheritance.applications.find(
        (a) => a.applicationId === selectedAppId,
      );
      const ids =
        app?.services
          ?.filter((s) => s.isUsed === true)
          .map((s) => s.serviceId) ?? [];
      setSelectedIds(ids);
    } else {
      setSelectedIds([]);
    }
  }, [
    open,
    mode,
    selectedAppId,
    selectedServiceId,
    inheritance.applications,
  ]);

  const selectedApp = useMemo(
    () =>
      inheritance.applications.find(
        // selectedAppId is the applicationId UUID. Match strictly on UUID
        // — appCode collisions (multiple apps with appCode "A360") would
        // otherwise resolve to whichever app appears first in the list.
        (a) => a.applicationId === selectedAppId,
      ) ?? null,
    [inheritance.applications, selectedAppId],
  );

  // Flatten components for the layer picker.
  const allComponents: LayerItem[] = useMemo(() => {
    const seen = new Set<string>();
    const out: LayerItem[] = [];
    for (const app of inheritance.applications) {
      for (const c of app.components ?? []) {
        if (seen.has(c.componentId)) continue;
        seen.add(c.componentId);
        out.push({
          id: c.componentId,
          kind: "component",
          title: c.title,
          layer: "",
          description: "",
          components: (c.devices ?? []).map((d) => d.name),
        });
      }
      for (const svc of app.services ?? []) {
        for (const c of svc.components ?? []) {
          if (seen.has(c.componentId)) continue;
          seen.add(c.componentId);
          out.push({
            id: c.componentId,
            kind: "component",
            title: c.title,
            layer: "",
            description: "",
            components: (c.devices ?? []).map((d) => d.name),
          });
        }
      }
    }
    return out;
  }, [inheritance.applications]);

  const serviceLayer: LayerItem[] = useMemo(() => {
    const seen = new Set<string>();
    const out: LayerItem[] = [];
    for (const app of inheritance.applications) {
      for (const svc of app.services ?? []) {
        if (seen.has(svc.serviceId)) continue;
        seen.add(svc.serviceId);
        out.push({
          id: svc.serviceId,
          kind: "ssp",
          title: svc.name,
          layer: svc.serviceType || "Service",
          description: svc.description || "",
          components: (svc.components ?? []).map((c) => c.title),
        });
      }
    }
    return out;
  }, [inheritance.applications]);

  const cylinders: Cylinder[] = useMemo(() => {
    // Application mode → one outer card representing the chosen
    // application. Per the latest BE contract, only the application's
    // services are user-pickable here (each rendered as a SERVICE chip
    // whose id is the serviceId). Direct components on the app are
    // intentionally not surfaced.
    if (mode === "application") {
      if (!selectedApp) return [];

      const items: LayerItem[] = (selectedApp.services ?? []).map((svc) => {
        // Only services with at least one linked SSP can be inherited.
        // Services with isUsed=false aren't authorized yet, so the chip
        // is rendered disabled and the user gets a tooltip explaining why.
        const usable = svc.isUsed === true;
        return {
          id: svc.serviceId,
          kind: "ssp",
          title: svc.name,
          layer: svc.serviceType || "Service",
          description: svc.description || "",
          components: (svc.components ?? []).map((c) => c.title),
          isSelectable: usable,
          disabledReason: usable
            ? undefined
            : "This service has no SSP yet  it can't be inherited until one is created.",
          linkedSsps: svc.usedBySsps ?? [],
        };
      });

      if (items.length === 0) return [];

      return [
        {
          key: "application",
          label: `Application: ${selectedApp.applicationName}`,
          tone: indigoTone,
          items,
        },
      ];
    }
    // Service mode → drill into the chosen service and show its components.
    // If the user hasn't picked one yet, fall back to listing every service.
    if (selectedServiceId) {
      const items: LayerItem[] = [];
      for (const a of inheritance.applications) {
        for (const s of a.services ?? []) {
          if (s.serviceId !== selectedServiceId) continue;
          for (const c of s.components ?? []) {
            items.push({
              id: c.componentId,
              kind: "component",
              title: c.title,
              layer: c.componentType || "Component",
              description: "",
              components: (c.devices ?? []).map((d) => d.name),
            });
          }
        }
      }
      return [
        { key: "service", label: "Service", tone: indigoTone, items },
      ];
    }
    return [
      {
        key: "service",
        label: "Service",
        tone: indigoTone,
        items: serviceLayer,
      },
    ];
  }, [mode, selectedApp, allComponents, serviceLayer, selectedServiceId, inheritance.applications]);

  // ── Real graph data for the inline tree preview ──
  // Application mode: the chosen app is the root, its services are SSPs,
  //                   and each service's components carry devices as inv.
  // Service mode:     the chosen service is the root SSP, with its
  //                   components and their devices below.
  // Filter to the user's preview pick (or current selection) so the tree
  // only shows what's relevant.
  const previewAppData: AppData = useMemo(() => {
    const focusIds = previewId ? [previewId] : selectedIds;
    const focus = new Set(focusIds);

    const componentToCompNode = (
      c: InheritedComponent,
      tag?: string,
    ) => ({
      id: c.componentId,
      name: c.title,
      type: c.componentType || "Component",
      status: "operational" as const,
      tag,
      inventory: (c.devices ?? []).map((d) => ({
        id: d.id,
        hostname: d.name,
        ip: d.ipAddress || "—",
        status: (d.status === "ACTIVE"
          ? "Active"
          : d.status === "STANDBY"
            ? "Standby"
            : "DR") as "Active" | "Standby" | "DR",
        device: d,
      })),
      // BE doesn't expose inherited controls yet — populate when available.
      controls: [] as { id: string; controlId: string; title: string }[],
    });

    if (mode === "application") {
      // `selectedAppId` is the applicationId UUID coming from the picker
      // dropdown. Match on UUID — appCode collisions and the old silent
      // fallback to `applications[0]` would otherwise render the wrong
      // application's tree (e.g. picking app #2 shows app #1's data).
      const app = inheritance.applications.find(
        (a) => a.applicationId === selectedAppId,
      );
      if (!app) {
        return {
          appName: "Application",
          appSubtitle: "Inheritance Flow Visualization",
          totalInventory: 0,
          totalComponents: 0,
          totalSatisfied: 0,
          totalNotSatisfied: 0,
          ssps: [],
        };
      }
      // Each service becomes one SSP node; its components become CompNodes.
      const sspNodes = (app.services ?? [])
        .filter((s) => focus.size === 0 || focus.has(s.serviceId))
        .map((s: InheritedService) => ({
          id: s.serviceId,
          title: s.name,
          subtitle: s.serviceType || "Service",
          color: "violet",
          sspCount: `${(s.components ?? []).length} comp`,
          components: (s.components ?? []).map((c) =>
            componentToCompNode(c, s.serviceType),
          ),
        }));
      // Directly-attached components: bundle under a synthetic "Components"
      // SSP so the tree has a uniform App → SSP → Comp shape.
      const direct = (app.components ?? []).filter(
        (c) => focus.size === 0 || focus.has(c.componentId),
      );
      if (direct.length > 0) {
        sspNodes.unshift({
          id: `${app.applicationId}-direct`,
          title: "Direct Components",
          subtitle: "Application owned",
          color: "indigo",
          sspCount: `${direct.length} comp`,
          components: direct.map((c) => componentToCompNode(c, "App")),
        });
      }
      const allComps = sspNodes.flatMap((s) => s.components);
      const totalInv = allComps.reduce((n, c) => n + c.inventory.length, 0);
      return {
        appName: app.applicationName,
        appSubtitle: app.appCode,
        totalInventory: totalInv,
        totalComponents: allComps.length,
        totalSatisfied: 0,
        totalNotSatisfied: 0,
        ssps: sspNodes,
      };
    }
    // Service mode — root is the chosen service (or all services if none chosen).
    // `focus` here holds component IDs (chips in Service mode are components),
    // so use it to filter the service's components, not the service itself.
    const services: { svc: InheritedService; appName: string }[] = [];
    for (const a of inheritance.applications) {
      for (const s of a.services ?? []) {
        if (selectedServiceId && s.serviceId !== selectedServiceId) continue;
        services.push({ svc: s, appName: a.applicationName });
      }
    }
    const sspNodes = services.map(({ svc, appName }) => {
      const comps = (svc.components ?? []).filter(
        (c) => focus.size === 0 || focus.has(c.componentId),
      );
      return {
        id: svc.serviceId,
        title: svc.name,
        subtitle: appName,
        color: "violet",
        sspCount: `${comps.length} comp`,
        components: comps.map((c) => componentToCompNode(c, svc.serviceType)),
      };
    });
    const allComps = sspNodes.flatMap((s) => s.components);
    return {
      appName:
        services[0]?.svc.name ||
        (selectedServiceId ? "Service" : "Services"),
      appSubtitle: "Inheritance Flow Visualization",
      totalInventory: allComps.reduce((n, c) => n + c.inventory.length, 0),
      totalComponents: allComps.length,
      totalSatisfied: 0,
      totalNotSatisfied: 0,
      ssps: sspNodes,
      // Service mode has no Application above the service — the service
      // itself is the natural root of the graph.
      hideAppRoot: true,
    };
  }, [
    mode,
    selectedAppId,
    selectedServiceId,
    inheritance.applications,
    previewId,
    selectedIds,
  ]);

  const toggle = (id: string) =>
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );

  if (!open) return null;

  // Resolve the chosen service (Service mode) for header copy.
  const selectedService = (() => {
    if (mode !== "service" || !selectedServiceId) return null;
    for (const a of inheritance.applications) {
      for (const s of a.services ?? []) {
        if (s.serviceId === selectedServiceId) return s;
      }
    }
    return null;
  })();

  // In preview mode, show the chosen application/service name instead of
  // the generic "Inheritance Tree Preview" header so the user always knows
  // what they're looking at.
  const previewName = selectedApp
    ? selectedApp.applicationName
    : selectedService
      ? selectedService.name
      : "Inheritance";

  const headerTitle = showPreview
    ? previewName
    : selectedApp
      ? `${selectedApp.applicationName} inheritance`
      : selectedService
        ? `${selectedService.name} inheritance`
        : "Configure inheritance";

  const headerSubtitle = showPreview
    ? ""
    : selectedApp
      ? `Pick the components your new SSP will inherit from ${selectedApp.applicationName}. Click an eye to inspect its tree.`
      : selectedService
        ? `Pick the components your new SSP will inherit from ${selectedService.name}. Click an eye to inspect its tree.`
        : "Pick the SSPs and Components your new SSP will inherit. Click an eye to inspect its tree.";

  const primaryBtnCls =
    mode === "service"
      ? "bg-violet-600 hover:bg-violet-700 dark:bg-violet-500 dark:hover:bg-violet-600"
      : "bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-600 dark:hover:bg-indigo-700";

  /* ── chip ──────────────────────────────────────────── */
  const renderChip = (item: LayerItem, tone: Tone) => {
    const isSelected = selectedIds.includes(item.id);
    const isSsp = item.kind === "ssp";
    const TypeIcon = isSsp ? ShieldCheck : Server;
    // When isSelectable is explicitly false, the chip is locked: clicks
    // are blocked, the radio + eye are visually dimmed, and a styled
    // tooltip on hover/focus explains why (and lists linked SSPs if any).
    const disabled = item.isSelectable === false;

    return (
      <div key={item.id} className="relative group/chip">
        <button
          type="button"
          onClick={() => {
            if (disabled) return;
            toggle(item.id);
          }}
          disabled={disabled}
          aria-disabled={disabled}
          className={`w-full relative flex items-center gap-2.5 rounded-lg border-2 pl-2.5 pr-2 py-2.5 text-left group min-w-0 transition-[transform,box-shadow,background-color,border-color] duration-150 ease-out ${
            disabled
              ? "border-muted-foreground/25 bg-card/80 dark:border-white/10 dark:bg-white/[0.04] cursor-default"
              : isSelected
                ? `${tone.selectedBorder} ${tone.selectedBg} shadow-lg hover:-translate-y-px ring-2 ring-offset-0`
                : "border-border bg-white/70 dark:bg-card/70 hover:border-muted-foreground/40 hover:bg-white hover:shadow-sm dark:hover:bg-card hover:-translate-y-px"
          }`}
          style={
            !disabled && isSelected
              ? {
                  boxShadow: `0 4px 16px -4px ${tone.glow}, 0 0 0 1px ${tone.border}`,
                }
              : undefined
          }
        >
        {/* Radio is hidden on disabled chips since they aren't selectable
            — clean look, no greyed-out checkbox to confuse users. */}
        {!disabled && (
          <div
            className={`size-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all ${
              isSelected
                ? `${tone.radioBorder} ${tone.radioFill}`
                : "border-muted-foreground/30 group-hover:border-muted-foreground/60"
            }`}
          >
            {isSelected && (
              <Check className="size-2.5 text-white" strokeWidth={3.5} />
            )}
          </div>
        )}
        <TypeIcon
          className={`size-3.5 flex-shrink-0 transition-opacity ${
            disabled
              ? "opacity-70"
              : isSelected
                ? "opacity-100"
                : "opacity-55 group-hover:opacity-90"
          }`}
          style={{ color: tone.labelText }}
        />
        <div className="flex-1 min-w-0">
          <span
            className={`block text-[12px] font-medium truncate ${
              disabled ? "text-muted-foreground/85" : "text-foreground"
            }`}
          >
            {item.title}
          </span>
          {item.parentService && (
            <span
              className="block text-[9.5px] text-muted-foreground/80 truncate"
              title={`From service: ${item.parentService}`}
            >
              from {item.parentService}
            </span>
          )}
        </div>
        <span
          className={`flex-shrink-0 text-[8.5px] font-extrabold tracking-[0.12em] ${
            disabled ? "text-muted-foreground/70" : "text-muted-foreground/70"
          }`}
        >
          {isSsp ? "SERVICE" : "COMP"}
        </span>
        {/* Spacer so the absolutely-positioned eye doesn't overlap the kind
            marker text — same width as the eye button below. */}
        <span className="size-6 flex-shrink-0" aria-hidden />
      </button>

      {/* Eye — rendered as a sibling overlay so it remains clickable even
          when the parent <button> is disabled. Stays bright on disabled
          chips so the user can still preview the inheritance tree. */}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          if (!disabled && !isSelected) toggle(item.id);
          setPreviewId(item.id);
          setShowPreview(true);
        }}
        aria-label="Preview inheritance tree"
        className={`absolute top-1/2 -translate-y-1/2 right-2.5 z-10 p-1 rounded transition-colors flex-shrink-0 cursor-pointer ${
          disabled
            ? "text-muted-foreground/85 hover:text-foreground hover:bg-foreground/10"
            : "text-muted-foreground/50 hover:text-foreground hover:bg-foreground/10"
        }`}
      >
        <Eye className="size-3.5" />
      </button>

      {/* Custom tooltip — shows on hover/focus when the chip is disabled.
          Positioned absolutely above the chip; uses group-hover so it's
          purely CSS-driven (no extra state). Pointer-events disabled so
          the tooltip itself doesn't intercept hover. */}
      {disabled && (
        <div
          role="tooltip"
          className="pointer-events-none absolute left-1/2 bottom-full mb-2 -translate-x-1/2 z-30 w-64 max-w-[280px] rounded-lg bg-[#0f172a] dark:bg-[#0b1322] border border-white/10 shadow-xl px-3 py-2 opacity-0 group-hover/chip:opacity-100 group-focus-within/chip:opacity-100 transition-opacity duration-150"
        >
          <p className="text-[11px] leading-relaxed text-white/85">
            {item.disabledReason || "Not available for inheritance."}
          </p>
          {item.linkedSsps && item.linkedSsps.length > 0 && (
            <p className="mt-1.5 text-[10px] leading-relaxed text-white/60">
              <span className="font-semibold text-white/75">
                Already linked to:
              </span>{" "}
              {item.linkedSsps.map((s) => s.title).join(", ")}
            </p>
          )}
          {/* Tail */}
          <span
            className="absolute left-1/2 -bottom-1 -translate-x-1/2 size-2 rotate-45 bg-[#0f172a] dark:bg-[#0b1322] border-r border-b border-white/10"
            aria-hidden
          />
        </div>
      )}
      </div>
    );
  };

  /* ── layer card ────────────────────────────────────── */
  const renderLayerCard = (cyl: Cylinder) => {
    const ssps = cyl.items.filter((i) => i.kind === "ssp");
    const directComps = cyl.items.filter(
      (i) => i.kind === "component" && i.bucket !== "service",
    );
    const serviceComps = cyl.items.filter(
      (i) => i.kind === "component" && i.bucket === "service",
    );
    const totalCount = cyl.items.length;
    const isEmpty = totalCount === 0;
    const pickedInLayer = cyl.items.filter((i) =>
      selectedIds.includes(i.id),
    ).length;
    // Single neutral icon for every layer card — the card's label and
    // tone do the visual differentiation, so adding a new bucket from the
    // BE (e.g. "cloud") doesn't need any icon mapping here.
    const Icon = Layers;
    // Drop the trailing "Layer" suffix when the label already names the
    // bucket explicitly (e.g. "Application: HRMS").
    const heading = /:\s/.test(cyl.label) ? cyl.label : `${cyl.label} Layer`;

    return (
      <div
        key={cyl.key}
        className="group/card rounded-xl bg-card border border-border hover:border-muted-foreground/30 transition-colors"
      >
        <div className="flex">
          <div
            className="w-1 rounded-l-xl flex-shrink-0"
            style={{ background: cyl.tone.labelText }}
          />
          <div className="flex-1 min-w-0 p-4">
            <div className="flex items-center gap-2.5 mb-3">
              <div
                className="size-7 rounded-md flex items-center justify-center flex-shrink-0"
                style={{ background: `${cyl.tone.labelText}1a` }}
              >
                <Icon
                  className="size-3.5"
                  style={{ color: cyl.tone.labelText }}
                  strokeWidth={2.25}
                />
              </div>
              <h3 className="text-sm font-bold text-foreground">{heading}</h3>
              <span className="text-[11px] font-medium text-foreground/70">
                {totalCount} item{totalCount !== 1 ? "s" : ""}
              </span>
              <div className="flex-1" />
              {pickedInLayer > 0 && (
                <span
                  className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold animate-in fade-in zoom-in-95 duration-150"
                  style={{
                    background: cyl.tone.labelText,
                    color: "#ffffff",
                  }}
                >
                  <Check className="size-2.5" strokeWidth={3.5} />
                  {pickedInLayer} selected
                </span>
              )}
            </div>

            <div className="space-y-4">
              {isEmpty && (
                <p className="text-center text-xs text-muted-foreground py-3">
                  No items in this layer for the selected application.
                </p>
              )}

              {ssps.length > 0 && (
                <div>
                  <div className="flex items-center gap-1.5 mb-2">
                    <ShieldCheck
                      className="size-3 flex-shrink-0"
                      style={{ color: cyl.tone.labelText }}
                      strokeWidth={2.5}
                    />
                    <span
                      className="text-[10px] font-bold tracking-wider"
                      style={{ color: cyl.tone.labelText }}
                    >
                      Services
                    </span>
                    <span className="text-[10px] font-semibold text-foreground/60">
                      · {ssps.length}
                    </span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {ssps.map((it) => renderChip(it, cyl.tone))}
                  </div>
                </div>
              )}

              {directComps.length > 0 && (
                <div>
                  <div className="flex items-center gap-1.5 mb-2">
                    <Square
                      className="size-3 flex-shrink-0"
                      style={{ color: cyl.tone.labelText }}
                      strokeWidth={2.5}
                    />
                    <span
                      className="text-[10px] font-bold tracking-wider"
                      style={{ color: cyl.tone.labelText }}
                    >
                      Direct Components
                    </span>
                    <span className="text-[10px] font-semibold text-foreground/60">
                      · {directComps.length}
                    </span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {directComps.map((it) => renderChip(it, cyl.tone))}
                  </div>
                </div>
              )}

              {serviceComps.length > 0 && (
                <div>
                  <div className="flex items-center gap-1.5 mb-2">
                    <Server
                      className="size-3 flex-shrink-0"
                      style={{ color: cyl.tone.labelText }}
                      strokeWidth={2.5}
                    />
                    <span
                      className="text-[10px] font-bold tracking-wider"
                      style={{ color: cyl.tone.labelText }}
                    >
                      Service Components
                    </span>
                    <span className="text-[10px] font-semibold text-foreground/60">
                      · {serviceComps.length}
                    </span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {serviceComps.map((it) => renderChip(it, cyl.tone))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  /* ── render ────────────────────────────────────────── */
  const handleConfirm = () => {
    // Resolve the final component_ids list:
    //   1. Expand each picked service to its underlying component IDs (BE wants componentIds only).
    //   2. Auto-include the application's direct components so the new SSP also inherits everything at the app level.
    //   3. Dedupe across services that share the same comps.
    const serviceMap = new Map<string, string[]>();
    for (const a of inheritance.applications) {
      for (const s of a.services ?? []) {
        serviceMap.set(
          s.serviceId,
          (s.components ?? []).map((c) => c.componentId),
        );
      }
    }
    const expanded = new Set<string>();
    for (const id of selectedIds) {
      const childComps = serviceMap.get(id);
      if (childComps) {
        for (const c of childComps) expanded.add(c);
      } else {
        expanded.add(id);
      }
    }
    if (mode === "application" && selectedApp) {
      for (const c of selectedApp.components ?? []) {
        expanded.add(c.componentId);
      }
    }
    // Derive provider SSP IDs from selected services so Leveraged Authorizations
    // are created automatically — mirrors what Step 1's checkbox used to do.
    const providerSspIds = serviceProviderSsps
      .filter((sp) => selectedIds.includes(sp.serviceId) && sp.sspId && sp.isPublished)
      .map((sp) => sp.sspId as string);

    onConfirm(Array.from(expanded), {
      applicationId: selectedApp?.applicationId,
      serviceId: selectedService?.serviceId,
      providerSspIds,
    });
  };

  const previewTitle = showPreview ? previewName : headerTitle;
  const previewDescription = showPreview
    ? "Inheritance flow preview click any node to see its details. Use the back arrow to return to the picker."
    : headerSubtitle || undefined;

  const footer = showPreview ? undefined : (
    <>
      <button
        type="button"
        onClick={onClose}
        className="px-4 py-2 rounded-lg border border-[#e2e8f0] dark:border-white/[0.07] text-sm font-medium text-[#475569] dark:text-white/60 hover:bg-[#f1f5f9] dark:hover:bg-white/[0.04] transition-colors"
      >
        Cancel
      </button>
      <button
        type="button"
        onClick={handleConfirm}
        disabled={creating}
        className={`px-4 py-2 rounded-lg ${primaryBtnCls} text-white text-sm font-bold transition-colors disabled:opacity-40 shadow-md`}
      >
        {creating
          ? "Creating SSP"
          : `Create SSP${
              selectedIds.length > 0
                ? ` with ${selectedIds.length} Inherited`
                : ""
            }`}
      </button>
    </>
  );

  const handleExitPreview = () => {
    setShowPreview(false);
    setPreviewId(null);
  };

  return (
    <Modal
      open
      onClose={onClose}
      onBack={showPreview ? handleExitPreview : (onBack ?? onClose)}
      title={previewTitle}
      description={previewDescription}
      footer={footer}
    >
      {showPreview ? (
        <div className="h-[calc(85vh-9rem)] -mx-1 -my-1 rounded-lg overflow-hidden bg-card/60 border border-border">
          <ReactFlowProvider>
            <InheritanceFlowView
              appName={selectedApp?.applicationName || "Application"}
              selectedSspIds={previewId ? [previewId] : selectedIds}
              data={previewAppData}
              embedded
              onBack={handleExitPreview}
            />
          </ReactFlowProvider>
        </div>
      ) : inheritanceLoading ? (
        <div className="flex flex-col items-center justify-center gap-3 py-16">
          <Loader2 className="size-8 animate-spin text-indigo-500 dark:text-indigo-400" />
          <p className="text-sm text-muted-foreground">
            Loading {mode === "service" ? "services" : "applications"}…
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {cylinders.map((cyl) => renderLayerCard(cyl))}
        </div>
      )}
    </Modal>
  );
}
